import { CART_SOURCES } from './cartSourceIsolation';
import { buildCartLayout, normalizeSectionTitle } from './cartLayout';

export const CART_WORKSPACE_SOURCES = CART_SOURCES;

const PRODUCT_LINE_KEY = 'cartLineId';
const SERVICE_LINE_KEY = 'cartServiceId';
const SECTION_LINE_KEY = 'sectionId';

const asArray = (value) => (Array.isArray(value) ? value : []);

const stableFallbackId = (prefix, source, index) => `${source || CART_WORKSPACE_SOURCES.ORDER}:${prefix}:${index}`;

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);
const firstPresent = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);
const hasOwnAny = (object, keys = []) => keys.some((key) => hasOwn(object, key));
const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== '';

const toNumber = (...values) => {
  const value = firstDefined(...values);
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const isValidTaxId = (value) => {
  if (!hasValue(value)) return false;
  if (Number(value) === 0) return false;
  return true;
};

const normalizeTaxIds = (...values) => {
  const ids = [];
  values.forEach((value) => {
    const candidates = Array.isArray(value) ? value : [value];
    candidates.forEach((candidate) => {
      if (!isValidTaxId(candidate)) return;
      if (!ids.some((id) => String(id) === String(candidate))) ids.push(candidate);
    });
  });
  return ids;
};

const getSupplierName = (value) => {
  if (typeof value === 'string') return value;
  return value?.Name ?? value?.name;
};

const getSupplierId = (value) => {
  if (!value || typeof value !== 'object') return undefined;
  return value.Id ?? value.id ?? value.SupplierId ?? value.supplierId;
};

const getWorkspacePricingRows = (product = {}) => {
  const rows = firstDefined(
    product.pricings,
    product.Pricings,
    product.Pricing
  );
  return Array.isArray(rows) ? rows : [];
};

const hasLightweightPricingValues = (product = {}) => [
  product.NetCost,
  product.netCost,
  product.Cost,
  product.cost,
  product.Retail,
  product.retail,
  product.ClientPrice,
  product.clientPrice,
  product.Total,
  product.total,
].some(hasValue);

export const normalizeNewProductForCartWorkspace = (product = {}) => {
  const next = { ...product };
  const existingPricings = getWorkspacePricingRows(next);
  if (existingPricings.length || !hasLightweightPricingValues(next)) return next;

  const quantity = toNumber(next.Quantity, next.quantity) || 1;
  const netCost = toNumber(next.NetCost, next.netCost, next.Cost, next.cost);
  const marginValue = Number(firstDefined(next.Margin, next.margin));
  const retail = toNumber(next.Retail, next.retail);
  const clientPrice = toNumber(next.ClientPrice, next.clientPrice, next.Retail, next.retail);
  const total = toNumber(next.Total, next.total, clientPrice * quantity);

  return {
    ...next,
    Pricing: [{
      id: null,
      SKU: firstDefined(next.SKU, next.sku, next.Number, ''),
      Size: firstDefined(next.Size, next.size, ''),
      Color: firstDefined(next.Color, next.color, next.selectedColor, ''),
      Type: 'BreakDown',
      Quantity: quantity,
      NetCost: netCost,
      Margin: Number.isFinite(marginValue) ? marginValue : 40,
      Retail: retail,
      ClientPrice: clientPrice,
      Total: total,
      DisplayStatus: 'Display to Client',
      IsLocked: Boolean(firstDefined(next.IsLocked, next.isLocked)),
      IsRetailLocked: Boolean(firstDefined(next.IsRetailLocked, next.isRetailLocked)),
      OrderItemId: null,
      orderItemId: null,
      persistedOrderItemId: null,
    }],
  };
};

const normalizePricingRow = (pricing = {}, product = {}) => {
  const quantity = toNumber(
    pricing.Quantity,
    pricing.quantity
  );

  const netCost = toNumber(
    pricing.NetCost,
    pricing.netCost,
    pricing.Cost,
    pricing.cost
  );
  const convertedNetCostValue = firstDefined(pricing.ConvertedNetCost, pricing.convertedNetCost);
  const exchangeRateValue = firstDefined(pricing.ExchangeRate, pricing.exchangeRate);

  const margin = toNumber(
    pricing.Margin,
    pricing.margin
  );

  const retail = toNumber(
    pricing.Retail,
    pricing.retail
  );

  const clientPrice = toNumber(
    pricing.ClientPrice,
    pricing.clientPrice,
    pricing.Retail,
    pricing.retail
  );

  const total = toNumber(
    pricing.Total,
    pricing.total,
    clientPrice * quantity
  );

  const sku = firstDefined(
    pricing.SKU,
    pricing.sku,
    product.SKU,
    product.sku,
    ''
  );

  const size = firstDefined(
    pricing.Size,
    pricing.size,
    product.Size,
    product.size,
    ''
  );

  const color = firstDefined(
    pricing.Color,
    pricing.color,
    product.Color,
    product.color,
    ''
  );

  const type = firstDefined(
    pricing.Type,
    pricing.type,
    'BreakDown'
  );

  const displayStatus = firstDefined(
    pricing.DisplayStatus,
    pricing.displayStatus,
    'Display to Client'
  );

  const isLocked = firstDefined(
    pricing.IsLocked,
    pricing.isLocked,
    false
  );
  const isRetailLocked = firstDefined(
    pricing.IsRetailLocked,
    pricing.isRetailLocked,
    false
  );

  const orderItemId = firstDefined(
    pricing.persistedOrderItemId,
    pricing.OrderItemId,
    pricing.orderItemId,
    product.persistedOrderItemId,
    product.OrderItemId,
    product.orderItemId
  ) ?? null;

  return {
    ...pricing,

    id: firstDefined(pricing.id, pricing.Id, null),

    SKU: sku,
    sku,

    Size: size,
    size,

    Color: color,
    color,

    Type: type,
    type,

    Quantity: quantity,
    quantity,

    NetCost: netCost,
    netCost,
    ...(convertedNetCostValue !== undefined ? { ConvertedNetCost: convertedNetCostValue } : {}),
    ...(exchangeRateValue !== undefined ? { ExchangeRate: exchangeRateValue } : {}),

    Margin: margin,
    margin,

    Retail: retail,
    retail,

    ClientPrice: clientPrice,
    clientPrice,

    Total: total,
    total,

    DisplayStatus: displayStatus,
    displayStatus,

    IsLocked: isLocked,
    isLocked,
    IsRetailLocked: isRetailLocked,
    isRetailLocked,

    OrderItemId: orderItemId,
    orderItemId,
  };
};

const normalizeCartProduct = (product = {}, index = 0, source = CART_WORKSPACE_SOURCES.ORDER) => {
  const sourceProduct = product?.source && typeof product.source === 'object'
    ? product.source
    : {};

  // Snapshot rows often keep the live order item under `source`.
  // Merge `source` first, then product second so invoice-cart edits can override snapshot values.
  const merged = {
    ...sourceProduct,
    ...product,
  };

  const productId = firstDefined(
    merged.ProductId,
    merged.productId,
    sourceProduct.ProductId,
    sourceProduct.productId
  );

  const productHasPersistedIdentity = hasOwnAny(product, [
    'persistedOrderItemId',
    'PersistedOrderItemId',
    'orderItemId',
    'OrderItemId',
    'dbItemId',
    'DBItemId',
  ]);

  const explicitPersistedOrderItemId = productHasPersistedIdentity
    ? firstPresent(
      product.persistedOrderItemId,
      product.PersistedOrderItemId,
      product.orderItemId,
      product.OrderItemId,
      product.dbItemId,
      product.DBItemId
    )
    : firstPresent(
    merged.persistedOrderItemId,
    merged.PersistedOrderItemId,
    merged.orderItemId,
    merged.OrderItemId,
    merged.dbItemId,
    merged.DBItemId,
    sourceProduct.orderItemId,
    sourceProduct.OrderItemId,
    sourceProduct.dbItemId,
    sourceProduct.DBItemId
  );

  const liveOrderItemId = source === CART_WORKSPACE_SOURCES.ORDER
    ? firstPresent(merged.id, merged.Id, sourceProduct.id, sourceProduct.Id)
    : null;

  const persistedOrderItemId = firstPresent(
    explicitPersistedOrderItemId,
    liveOrderItemId
  );

  const sourceOrderItemId = firstPresent(
    merged.sourceOrderItemId,
    merged.SourceOrderItemId,
    merged.originalOrderItemId,
    merged.OriginalOrderItemId,
    sourceProduct.sourceOrderItemId,
    sourceProduct.SourceOrderItemId,
    sourceProduct.originalOrderItemId,
    sourceProduct.OriginalOrderItemId,
    persistedOrderItemId
  );

  const name = firstDefined(
    merged.Name,
    merged.name,
    sourceProduct.Name,
    sourceProduct.name,
    ''
  );

  const supplierName = firstDefined(
    merged.SupplierName,
    merged.supplierName,
    getSupplierName(merged.Supplier),
    getSupplierName(merged.supplier),
    sourceProduct.SupplierName,
    sourceProduct.supplierName,
    getSupplierName(sourceProduct.Supplier),
    getSupplierName(sourceProduct.supplier),
    ''
  );

  const supplierId = firstDefined(
    merged.SupplierId,
    merged.supplierId,
    getSupplierId(merged.Supplier),
    getSupplierId(merged.supplier),
    sourceProduct.SupplierId,
    sourceProduct.supplierId,
    getSupplierId(sourceProduct.Supplier),
    getSupplierId(sourceProduct.supplier)
  );

  const currencyCode = firstDefined(
    merged.CurrencyCode,
    merged.currencyCode,
    sourceProduct.CurrencyCode,
    sourceProduct.currencyCode,
    'CAD'
  );

  const shortDescription = firstDefined(
    merged.ShortDescription,
    merged.shortDescription,
    sourceProduct.ShortDescription,
    sourceProduct.shortDescription,
    ''
  );

  const description = firstDefined(
    merged.Description,
    merged.description,
    sourceProduct.Description,
    sourceProduct.description,
    ''
  );

  const quantity = toNumber(
    merged.Quantity,
    merged.quantity,
    sourceProduct.Quantity,
    sourceProduct.quantity
  );

  const subtotal = toNumber(
    merged.Subtotal,
    merged.subtotal,
    sourceProduct.Subtotal,
    sourceProduct.subtotal,
    sourceProduct.Total,
    merged.Total
  );

  const rawPricings = asArray(
    firstDefined(
      merged.pricings,
      merged.Pricings,
      merged.Pricing,
      sourceProduct.pricings,
      sourceProduct.Pricings,
      sourceProduct.Pricing
    )
  );

  const normalizedPricings = rawPricings.map((pricing) =>
    normalizePricingRow(pricing, merged)
  );
  const productHasTaxIds = hasOwnAny(product, ['taxIds', 'TaxIds']);
  const existingTaxIds = productHasTaxIds
    ? normalizeTaxIds(product.taxIds, product.TaxIds)
    : normalizeTaxIds(sourceProduct.taxIds, sourceProduct.TaxIds);
  const fallbackTaxIds = normalizeTaxIds(firstDefined(merged.Tax, merged.tax, sourceProduct.Tax, sourceProduct.tax));
  // An explicit single-tax edit can update the persisted Tax/taxDetails while
  // leaving an older taxIds alias in the cart payload (e.g. GST 11 -> Exempt 44).
  // Only repair that stale single selection when both authoritative fields agree.
  // Never collapse a genuine multi-tax choice (such as GST + PST).
  const explicitTaxId = firstDefined(merged.Tax, merged.tax, sourceProduct.Tax, sourceProduct.tax);
  const detailsTaxId = firstDefined(merged.taxDetails?.taxId, sourceProduct.taxDetails?.taxId);
  const staleSingleTaxSelection = existingTaxIds.length === 1 &&
    isValidTaxId(explicitTaxId) &&
    isValidTaxId(detailsTaxId) &&
    String(explicitTaxId) === String(detailsTaxId) &&
    String(existingTaxIds[0]) !== String(explicitTaxId);
  const taxIds = staleSingleTaxSelection
    ? [explicitTaxId]
    : existingTaxIds.length ? existingTaxIds : fallbackTaxIds;
  const primaryTaxId = taxIds.length ? taxIds[0] : null;
  const taxDetails = firstDefined(
    merged.taxDetails,
    sourceProduct.taxDetails,
    primaryTaxId != null ? { taxId: primaryTaxId } : null
  );

  const normalized = {
    ...merged,

    id: firstDefined(
      product.id,
      product.Id,
      productHasPersistedIdentity ? null : firstDefined(sourceProduct.id, sourceProduct.Id),
      persistedOrderItemId
    ),
    lineId: firstDefined(
      merged.lineId,
      sourceProduct.lineId,
      persistedOrderItemId,
      productId ? `${productId}::${index}` : stableFallbackId('product-line', source, index)
    ),

    persistedOrderItemId: persistedOrderItemId ?? null,
    PersistedOrderItemId: persistedOrderItemId ?? null,
    sourceOrderItemId: sourceOrderItemId ?? null,
    SourceOrderItemId: sourceOrderItemId ?? null,
    originalOrderItemId: firstDefined(merged.originalOrderItemId, merged.OriginalOrderItemId, sourceOrderItemId, null),
    OriginalOrderItemId: firstDefined(merged.OriginalOrderItemId, merged.originalOrderItemId, sourceOrderItemId, null),
    dbItemId: persistedOrderItemId ?? null,
    DBItemId: persistedOrderItemId ?? null,
    orderItemId: persistedOrderItemId ?? null,
    OrderItemId: persistedOrderItemId ?? null,

    productId,
    ProductId: productId,

    Name: name,
    name,

    SupplierName: supplierName,
    supplierName,
    SupplierId: supplierId,
    supplierId,

    CurrencyCode: currencyCode,
    currencyCode,
    PricingCurrencyCode: firstDefined(merged.PricingCurrencyCode, merged.pricingCurrencyCode, sourceProduct.PricingCurrencyCode, sourceProduct.pricingCurrencyCode, 'CAD'),
    pricingCurrencyCode: firstDefined(merged.PricingCurrencyCode, merged.pricingCurrencyCode, sourceProduct.PricingCurrencyCode, sourceProduct.pricingCurrencyCode, 'CAD'),

    Source: firstDefined(
      merged.Source,
      merged.sourceType,
      sourceProduct.Source,
      sourceProduct.sourceType,
      'asi'
    ),

    ShortDescription: shortDescription,
    shortDescription,

    Description: description,
    description,

    Quantity: quantity,
    quantity,

    Subtotal: subtotal,
    subtotal,

    Tax: firstDefined(merged.Tax, merged.tax, sourceProduct.Tax, sourceProduct.tax, 0),
    tax: firstDefined(merged.tax, merged.Tax, sourceProduct.tax, sourceProduct.Tax, 0),
    taxId: staleSingleTaxSelection ? explicitTaxId : firstDefined(merged.taxId, merged.TaxId, sourceProduct.taxId, sourceProduct.TaxId, primaryTaxId),
    selectedTax: staleSingleTaxSelection ? String(explicitTaxId) : firstDefined(merged.selectedTax, sourceProduct.selectedTax, primaryTaxId != null ? String(primaryTaxId) : ''),
    taxDetails,
    taxIds,
    TaxIds: taxIds,

    supplier: firstDefined(merged.supplier, sourceProduct.supplier, null),

    Pricings: normalizedPricings,
    pricings: normalizedPricings,

    decorations: asArray(firstDefined(
      merged.decorations,
      merged.Decorations,
      merged.Decoration,
      sourceProduct.decorations,
      sourceProduct.Decorations,
      sourceProduct.Decoration
    )),
    artworks: asArray(firstDefined(
      merged.artworks,
      merged.Artworks,
      merged.ArtWorks,
      merged.ArtWork,
      sourceProduct.artworks,
      sourceProduct.Artworks,
      sourceProduct.ArtWorks,
      sourceProduct.ArtWork
    )),

    // Keep original source for later save/edit provenance.
    source: sourceProduct,
  };

  return ensureCartProductIdentity(normalized, index, source);
};

const normalizeCartService = (service = {}, index = 0, source = CART_WORKSPACE_SOURCES.ORDER) => {
  const sourceService = service?.source && typeof service.source === 'object'
    ? service.source
    : {};

  const merged = {
    ...sourceService,
    ...service,
  };

  const normalized = {
    ...merged,

    id: firstDefined(merged.id, merged.Id, sourceService.id, sourceService.Id),
    Type: firstDefined(merged.Type, merged.type, sourceService.Type, sourceService.type, ''),
    type: firstDefined(merged.type, merged.Type, sourceService.type, sourceService.Type, ''),

    Title: firstDefined(merged.Title, merged.title, sourceService.Title, sourceService.title, ''),
    title: firstDefined(merged.title, merged.Title, sourceService.title, sourceService.Title, ''),

    Description: firstDefined(merged.Description, merged.description, sourceService.Description, sourceService.description, ''),
    description: firstDefined(merged.description, merged.Description, sourceService.description, sourceService.Description, ''),

    Quantity: toNumber(merged.Quantity, merged.quantity, sourceService.Quantity, sourceService.quantity) || 1,
    quantity: toNumber(merged.quantity, merged.Quantity, sourceService.quantity, sourceService.Quantity) || 1,

    Total: toNumber(merged.Total, merged.total, sourceService.Total, sourceService.total),
    total: toNumber(merged.total, merged.Total, sourceService.total, sourceService.Total),

    UnitCost: toNumber(merged.UnitCost, merged.unitCost, sourceService.UnitCost, sourceService.unitCost),
    unitCost: toNumber(merged.unitCost, merged.UnitCost, sourceService.unitCost, sourceService.UnitCost),

    RetailPrice: toNumber(merged.RetailPrice, merged.retailPrice, sourceService.RetailPrice, sourceService.retailPrice),
    retailPrice: toNumber(merged.retailPrice, merged.RetailPrice, sourceService.retailPrice, sourceService.RetailPrice),

    Margin: toNumber(merged.Margin, merged.margin, sourceService.Margin, sourceService.margin),
    margin: toNumber(merged.margin, merged.Margin, sourceService.margin, sourceService.Margin),

    source: sourceService,
  };

  return ensureCartServiceIdentity(normalized, index, source);
};

export const ensureCartProductIdentity = (product = {}, index = 0, source = CART_WORKSPACE_SOURCES.ORDER) => {
  const next = { ...product };
  if (!next[PRODUCT_LINE_KEY]) {
    next[PRODUCT_LINE_KEY] = stableFallbackId('product', source, index);
  }
  return next;
};

export const ensureUniqueProductCartLineIds = (products = []) => {
  const reservedIds = new Set(products.map((product) => String(product?.[PRODUCT_LINE_KEY] ?? '')));
  const usedIds = new Set();
  const duplicateCounts = new Map();

  return products.map((product) => {
    const cartLineId = String(product?.[PRODUCT_LINE_KEY] ?? '');
    if (!usedIds.has(cartLineId)) {
      usedIds.add(cartLineId);
      duplicateCounts.set(cartLineId, 1);
      return product;
    }

    let occurrence = (duplicateCounts.get(cartLineId) || 1) + 1;
    let repairedId = `${cartLineId}:dup:${occurrence}`;
    while (usedIds.has(repairedId) || reservedIds.has(repairedId)) {
      occurrence += 1;
      repairedId = `${cartLineId}:dup:${occurrence}`;
    }
    duplicateCounts.set(cartLineId, occurrence);
    usedIds.add(repairedId);
    return { ...product, [PRODUCT_LINE_KEY]: repairedId };
  });
};

export const ensureCartServiceIdentity = (service = {}, index = 0, source = CART_WORKSPACE_SOURCES.ORDER) => {
  const next = { ...service };
  if (!next[SERVICE_LINE_KEY]) {
    next[SERVICE_LINE_KEY] = stableFallbackId('service', source, index);
  }
  return next;
};

export const ensureCartSectionIdentity = (section = {}, index = 0, source = CART_WORKSPACE_SOURCES.ORDER) => {
  const next = { ...section };
  if (!next[SECTION_LINE_KEY]) {
    next[SECTION_LINE_KEY] = stableFallbackId('section', source, index);
  }
  return next;
};

const normalizeCartSection = (section = {}, index = 0, source = CART_WORKSPACE_SOURCES.ORDER) => {
  const normalized = {
    ...section,
    id: firstDefined(section.id, section.sectionId, null),
    sectionId: firstDefined(section.sectionId, section.id, null),
    title: normalizeSectionTitle(section.title),
    sortOrder: toNumber(section.sortOrder, section.SortOrder, index),
  };

  return ensureCartSectionIdentity(normalized, index, source);
};

export const normalizeCartPayload = (input, fallbackOrder = {}, source = CART_WORKSPACE_SOURCES.ORDER) => {
  const candidate = input || fallbackOrder || {};
  const isLegacyArray = Array.isArray(candidate);

  const rawProducts = isLegacyArray
    ? candidate
    : (
        asArray(candidate?.Products).length
          ? candidate.Products
          : asArray(candidate?.products).length
            ? candidate.products
            : asArray(candidate?.payload?.Products).length
              ? candidate.payload.Products
              : asArray(candidate?.payload).length
                ? candidate.payload
                : candidate?.lines
      );

  const rawServices = isLegacyArray
    ? []
    : (
        asArray(candidate?.OtherService).length
          ? candidate.OtherService
          : asArray(candidate?.otherService).length
            ? candidate.otherService
            : asArray(candidate?.otherServices).length
              ? candidate.otherServices
              : asArray(candidate?.payload?.OtherService).length
              ? candidate.payload.OtherService
                : []
      );

  const rawSections = isLegacyArray
    ? []
    : (
        asArray(candidate?.cartSections).length
          ? candidate.cartSections
          : asArray(candidate?.CartSections).length
            ? candidate.CartSections
            : asArray(candidate?.payload?.cartSections).length
              ? candidate.payload.cartSections
              : []
      );
  const adjustments = isLegacyArray
    ? []
    : (
        asArray(candidate?.Adjustments).length
          ? candidate.Adjustments
          : asArray(candidate?.adjustments).length
            ? candidate.adjustments
            : asArray(candidate?.payload?.Adjustments).length
              ? candidate.payload.Adjustments
              : []
      );

  const products = ensureUniqueProductCartLineIds(
    asArray(rawProducts).map((product, index) =>
      normalizeCartProduct(product, index, source)
    )
  );

  const services = asArray(rawServices).map((service, index) =>
    normalizeCartService(service, index, source)
  );
  const cartSections = asArray(rawSections).map((section, index) =>
    normalizeCartSection(section, index, source)
  );
  const layout = buildCartLayout({ products, sections: cartSections });

  return {
    Products: products,
    OtherService: services,
    Adjustments: adjustments,
    cartSections,
    layout,
    meta: {
      ...(candidate?.meta || {}),
      ...(isLegacyArray ? { legacy: 'array' } : {}),
      source,
    },
  };
};

export const cloneCartPayload = (payload = {}) => {
  const source = payload?.meta?.source || CART_WORKSPACE_SOURCES.ORDER;
  const normalized = normalizeCartPayload(payload, undefined, source);
  // Snapshot editing must not retain shared Pricing/Decoration/Artwork
  // references between otherwise independent cart lines.
  return JSON.parse(JSON.stringify({
    Products: normalized.Products,
    OtherService: normalized.OtherService,
    Adjustments: normalized.Adjustments,
    cartSections: normalized.cartSections,
    layout: normalized.layout,
    meta: normalized.meta,
  }));
};

// Never render an Order Cart (or another invoice's cart) while the view
// already identifies itself as the Sales Order Cart. This can occur between
// a route change and the asynchronous snapshot hydration.
export const selectCartPayloadForSource = (payload, source) => {
  if (payload?.meta?.source === source) return payload;
  return {
    Products: [],
    OtherService: [],
    Adjustments: [],
    cartSections: [],
    layout: [],
    meta: { source },
  };
};

export const buildOrderForCartView = (order = {}, cartPayload) => {
  const normalized = normalizeCartPayload(cartPayload, order);
  return {
    ...order,
    Products: normalized.Products,
    OtherService: normalized.OtherService,
    cartSections: normalized.cartSections,
    layout: normalized.layout,
  };
};
